import { useEffect, useRef } from "react";
import { isEmbedded } from "./EmbeddedLoginPoller";
import { parentOrigin } from "./useEmbedHeight";

// Actions the chatbot on the parent page (oceanz.eu) may ask for, see
// oceanz-chatbot/docs/PROTOCOL.md. Messages come in as { type, id, ...args } and are answered with
// { type: "oceanz:result", id, ok, message, data? }. Nothing else from the parent is acted on.

// Chatbot target -> unique_name of the page in textData (urls/*).
const NAVIGATE_TARGETS = {
    upload: "home",
    cart: "checkout",
    orders: "account_orders",
    quotations: "account_quotations",
    library: "product_library",
    account: "account_details",
};

const NAME_PATTERN = /^[\w\-.[\]]{1,100}$/;
const ID_PATTERN = /^[\w\-.:]{1,100}$/;

const UPLOAD_EXTENSIONS = ["stl", "stp", "step", "3mf"];
const UPLOAD_MAX_BYTES = 200 * 1024 * 1024;
const UPLOAD_TIMEOUT = 10 * 60 * 1000;

// Returns a Dutch error message, or null when the file may be uploaded.
export const validateUploadFile = (file) => {
    if (typeof File === "undefined" || !(file instanceof File)) {
        return "Geen geldig bestand ontvangen.";
    }
    const extension = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (!UPLOAD_EXTENSIONS.includes(extension)) {
        return "Alleen STL-, STEP- en 3MF-bestanden zijn toegestaan.";
    }
    if (file.size <= 0) {
        return "Het bestand is leeg.";
    }
    if (file.size > UPLOAD_MAX_BYTES) {
        return "Het bestand is groter dan 200 MB.";
    }
    return null;
};

const findUploadInput = () => document.querySelector(".dragndrop input[type=file]");

const findUploadInputWhenReady = (timeout = 5000) =>
    new Promise((resolve) => {
        const started = Date.now();
        const look = () => {
            const input = findUploadInput();
            if (input || Date.now() - started >= timeout) {
                resolve(input);
                return;
            }
            setTimeout(look, 100);
        };
        look();
    });

const findCheckbox = (name) => {
    const escaped = window.CSS && CSS.escape ? CSS.escape(name) : name;
    return document.querySelector(
        `#app input[type="checkbox"]#${escaped}, #app input[type="checkbox"][name="${escaped}"], #app input[type="checkbox"][data-oceanz-name="${escaped}"]`
    );
};

const findCheckboxWhenReady = (name, timeout = 2000) =>
    new Promise((resolve) => {
        const started = Date.now();
        const look = () => {
            const box = findCheckbox(name);
            if (box || Date.now() - started >= timeout) {
                resolve(box);
                return;
            }
            setTimeout(look, 100);
        };
        look();
    });

export default function useEmbedActions({ navigateToPageName, authenticated, ready }) {
    // The listener is registered once; it reads the latest props through this ref.
    const latest = useRef({});
    latest.current = { navigateToPageName, authenticated, ready };

    useEffect(() => {
        if (!isEmbedded()) {
            return undefined;
        }
        // The framing origin is already restricted by the server (frame-ancestors); we only talk to it.
        const target = parentOrigin();
        if (!target) {
            return undefined;
        }

        const reply = (id, ok, message, data) => {
            const payload = { type: "oceanz:result", id, ok, message: String(message).slice(0, 200) };
            if (data) {
                payload.data = data;
            }
            window.parent.postMessage(payload, target);
        };

        // At most one upload at a time; Upload.jsx announces the outcome with an "oceanz:upload-done" event.
        let pendingUpload = null;
        const finishUpload = (ok, message, data) => {
            if (!pendingUpload) {
                return;
            }
            const { id, listener, timer } = pendingUpload;
            pendingUpload = null;
            window.removeEventListener("oceanz:upload-done", listener);
            clearTimeout(timer);
            reply(id, ok, message, data);
        };

        const startUpload = async (id, file, go) => {
            if (pendingUpload) {
                reply(id, false, "Er loopt al een upload.");
                return;
            }
            // Claim the slot before waiting, so a second message cannot slip in.
            pendingUpload = { id, listener: null, timer: null };
            let input = findUploadInput();
            if (!input) {
                go("home");
                input = await findUploadInputWhenReady();
            }
            if (!input) {
                // Upload is only rendered while no models are selected (Home.jsx).
                finishUpload(false, "Het uploadveld is nu niet beschikbaar; sluit eerst de huidige modelweergave.");
                return;
            }
            const listener = (e) => {
                const detail = e.detail || {};
                if (detail.name !== file.name) {
                    return;
                }
                if (detail.ok) {
                    finishUpload(true, "Geüpload.", {
                        modelId: detail.modelId,
                        name: String(detail.name).slice(0, 200),
                    });
                } else {
                    finishUpload(false, detail.error || "De upload is mislukt.");
                }
            };
            pendingUpload.listener = listener;
            pendingUpload.timer = setTimeout(
                () => finishUpload(false, "De upload duurde te lang of is mislukt."),
                UPLOAD_TIMEOUT
            );
            window.addEventListener("oceanz:upload-done", listener);
            // react-dropzone listens for the input's change event and calls onDrop with input.files.
            const transfer = new DataTransfer();
            transfer.items.add(file);
            input.files = transfer.files;
            input.dispatchEvent(new Event("change", { bubbles: true }));
        };

        const onMessage = (event) => {
            if (event.origin !== target || event.source !== window.parent) {
                return;
            }
            const data = event.data;
            if (
                !data ||
                typeof data !== "object" ||
                typeof data.type !== "string" ||
                !data.type.startsWith("oceanz:")
            ) {
                return;
            }
            if (data.type === "oceanz:result" || data.type === "oceanz:height") {
                return;
            }
            const id = typeof data.id === "string" && ID_PATTERN.test(data.id) ? data.id : null;
            if (!id) {
                return;
            }
            const { navigateToPageName: go, authenticated: loggedIn, ready: loaded } = latest.current;
            if (!loaded) {
                reply(id, false, "De pagina is nog aan het laden.");
                return;
            }

            if (data.type === "oceanz:navigate") {
                const uniqueName = typeof data.target === "string" ? NAVIGATE_TARGETS[data.target] : undefined;
                if (!uniqueName) {
                    reply(id, false, "Onbekend onderdeel.");
                    return;
                }
                if (!loggedIn) {
                    reply(id, false, "Je bent niet ingelogd, dus dit onderdeel is niet te openen.");
                    return;
                }
                const opened = go(uniqueName);
                reply(id, opened, opened ? "Geopend." : "Die pagina bestaat niet in deze omgeving.");
                return;
            }

            if (data.type === "oceanz:set") {
                if (
                    typeof data.name !== "string" ||
                    !NAME_PATTERN.test(data.name) ||
                    typeof data.checked !== "boolean"
                ) {
                    reply(id, false, "Ongeldige opdracht.");
                    return;
                }
                // A navigation just before this may still be rendering: give the box a moment to appear.
                findCheckboxWhenReady(data.name).then((box) => {
                    if (!box) {
                        reply(id, false, "Dat vakje staat niet op deze pagina.");
                        return;
                    }
                    if (box.disabled) {
                        reply(id, false, "Dat vakje is niet te wijzigen.");
                        return;
                    }
                    // click() so React's onChange runs, instead of flipping the DOM state behind its back.
                    if (box.checked !== data.checked) {
                        box.click();
                    }
                    const done = box.checked === data.checked;
                    reply(id, done, done ? "Gedaan." : "Het vakje liet zich niet wijzigen.");
                });
                return;
            }

            if (data.type === "oceanz:upload") {
                const problem = validateUploadFile(data.file);
                if (problem) {
                    reply(id, false, problem);
                    return;
                }
                if (!loggedIn) {
                    reply(id, false, "Je bent niet ingelogd, dus dit onderdeel is niet te openen.");
                    return;
                }
                startUpload(id, data.file, go);
                return;
            }

            reply(id, false, "Onbekende opdracht.");
        };

        window.addEventListener("message", onMessage);
        return () => {
            window.removeEventListener("message", onMessage);
            if (pendingUpload) {
                window.removeEventListener("oceanz:upload-done", pendingUpload.listener);
                clearTimeout(pendingUpload.timer);
                pendingUpload = null;
            }
        };
    }, []);
}
